import { formatCopy } from "../../../theme/copy";
import { Box, Typography } from "@mui/material";
import { useLayoutEffect, useRef } from "react";
import OthersRankSorter from "../../../components/ResponseComponents/OthersRankSorter";
import {
  commonContainer,
  commonWrapper,
} from "../../../components/ResponseComponents/styles";
import { useThemeContext } from "../../../globalContext/global-context";
import type { SurveyTranscriptEntry } from "../../../session/transcript";
import type { CustomMessageContent } from "../../../util/types";
import {
  activeQuestionGroupChild,
  isQuestionGroupContainerRaw,
  questionGroupChildRaw,
} from "../../questionGroup/core";
import { questionRegistry } from "../../registry";

export const shouldShowGroupHeading = (
  entries: readonly SurveyTranscriptEntry[],
  index: number,
) => {
  const entry = entries[index];
  if (
    entry.kind !== "question" ||
    !isQuestionGroupContainerRaw(entry.question.question)
  )
    return true;
  return !entries
    .slice(0, index)
    .some(
      (candidate) =>
        candidate.kind === "question" &&
        isQuestionGroupContainerRaw(candidate.question.question) &&
        candidate.question.question.group_id ===
          entry.question.question.group_id,
    );
};

export const HistoricalEntry = ({
  entry,
  showGroupHeading = true,
}: {
  entry: SurveyTranscriptEntry;
  showGroupHeading?: boolean;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const spacing = theme.tokens.spacing;
  if (entry.kind === "question") {
    const raw = entry.question.question;
    const promptFor = (questionRaw: typeof raw) => {
      const module = questionRegistry.get(questionRaw.type);
      return module
        ? module.prompt(module.core.decode(questionRaw))
        : {
            content: questionRaw.content,
            image: questionRaw.image,
            altText: questionRaw.alt_text ?? undefined,
            error: questionRaw.error_message,
          };
    };
    const prompts = isQuestionGroupContainerRaw(raw)
      ? [
          ...(showGroupHeading ? [{ content: raw.content }] : []),
          ...(() => {
            const child = activeQuestionGroupChild(raw);
            /* v8 ignore next -- valid groups always contain their active child */
            return child ? [promptFor(questionGroupChildRaw(child))] : [];
          })(),
        ]
      : [promptFor(raw)];
    return (
      <>
        {prompts.map((prompt, index) => {
          if (!prompt.content && !prompt.image && !prompt.error) return null;
          const message = {
            type: "text",
            content: prompt.content,
            self: false,
            image: prompt.image,
            alt_text: prompt.altText,
            error_message: prompt.error,
          } as CustomMessageContent;
          return (
            <Box
              key={index}
              sx={{
                paddingInline: `${spacing["4"]}px`,
                paddingTop: `${spacing["4"]}px`,
              }}
            >
              <OthersRankSorter message={message} timerImageOPen={false} />
            </Box>
          );
        })}
      </>
    );
  }
  const content =
    entry.kind === "answer"
      ? entry.projection.answer
      : entry.action === "undo"
        ? theme.copy.actions.undo
        : entry.action === "delete-stored-value"
          ? formatCopy(theme.copy.status.removed, {
              target: String(entry.target),
            })
          : entry.action === "auto-response"
            ? theme.copy.status.dontKnow
            : theme.copy.status.defaultResponse;
  return (
    <Box
      id={`session-message-${entry.attemptId}`}
      sx={{ paddingInline: `${spacing["4"]}px` }}
    >
      <Box sx={commonContainer(theme, true)}>
        <Box
          sx={{
            ...commonWrapper(theme, true, false),
            color: theme.tokens.color.action.primaryText,
            overflowWrap: "anywhere",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: theme.tokens.typography.size.md,
              lineHeight: theme.tokens.typography.lineHeight.normal,
              whiteSpace: "pre-wrap",
            }}
          >
            {content}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const CompletionTranscript = ({
  attemptId,
  entries,
}: {
  attemptId?: number;
  entries: readonly SurveyTranscriptEntry[];
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (scroll) scroll.scrollTop = scroll.scrollHeight;
  }, [attemptId]);
  return (
    <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {entries.map((entry, index) => (
        <HistoricalEntry
          key={`${entry.attemptId}-${entry.kind}-${index}`}
          entry={entry}
          showGroupHeading={shouldShowGroupHeading(entries, index)}
        />
      ))}
    </Box>
  );
};
